import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Heading,
  HStack,
  IconButton,
  SimpleGrid,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
  useToast,
} from '@chakra-ui/react';
import { Download, Trash2 } from 'lucide-react';
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
} from 'firebase/firestore';
import { db } from '../../services/firebase/firebase-config';

interface WaitlistDoc {
  id: string;
  email: string;
  name?: string;
  role?: string;
  referringCoach?: string | null;
  coachingChallenge?: string | null;
  source?: string;
  createdAt?: { seconds: number; nanoseconds: number };
}

const WAITLIST_COLLECTION = 'waitlist';

const formatTimestamp = (timestamp?: { seconds: number; nanoseconds: number }) => {
  if (!timestamp) return '—';
  try {
    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleString();
  } catch (error) {
    console.warn('Failed to format timestamp', error);
    return '—';
  }
};

const WaitlistAdminDashboard: React.FC = () => {
  const [waitlistEntries, setWaitlistEntries] = useState<WaitlistDoc[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    const waitlistQuery = query(
      collection(db, WAITLIST_COLLECTION),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      waitlistQuery,
      snapshot => {
        const entries: WaitlistDoc[] = snapshot.docs.map(docSnap => ({
          id: docSnap.id,
          ...(docSnap.data() as Omit<WaitlistDoc, 'id'>),
        }));
        setWaitlistEntries(entries);
        setIsLoading(false);
      },
      error => {
        console.error('Failed to load waitlist entries:', error);
        toast({
          title: 'Error loading waitlist',
          description: error.message,
          status: 'error',
          duration: 6000,
          isClosable: true,
        });
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [toast]);

  const totalCount = waitlistEntries.length;

  const roleBreakdown = useMemo(() => {
    const counts: Record<string, number> = {};
    waitlistEntries.forEach(entry => {
      const role = (entry.role || 'unknown').toLowerCase();
      counts[role] = (counts[role] || 0) + 1;
    });
    return counts;
  }, [waitlistEntries]);

  const deleteEntry = useCallback(
    async (entry: WaitlistDoc) => {
      const confirmed = window.confirm(
        `Delete waitlist entry for ${entry.email}? This cannot be undone.`
      );
      if (!confirmed) return;

      try {
        await deleteDoc(doc(db, WAITLIST_COLLECTION, entry.id));
        toast({
          title: 'Entry deleted',
          description: `${entry.email} was removed from the waitlist`,
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
      } catch (error: any) {
        console.error('Failed to delete waitlist entry:', error);
        toast({
          title: 'Delete failed',
          description: error.message,
          status: 'error',
          duration: 6000,
          isClosable: true,
        });
      }
    },
    [toast]
  );

  const exportCsv = useCallback(() => {
    if (waitlistEntries.length === 0) {
      toast({
        title: 'Nothing to export',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const headers = [
      'Email',
      'Name',
      'Role',
      'Referring Coach',
      'Coaching Challenge',
      'Source',
      'Created At',
    ];

    const rows = waitlistEntries.map(entry => [
      entry.email,
      entry.name || '',
      entry.role || '',
      entry.referringCoach || '',
      entry.coachingChallenge || '',
      entry.source || '',
      formatTimestamp(entry.createdAt),
    ]);

    const csvContent = [headers, ...rows]
      .map(row =>
        row
          .map(cell => {
            const safeCell = (cell ?? '').toString().replace(/"/g, '""');
            return `"${safeCell}"`;
          })
          .join(',')
      )
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `coach-core-waitlist-${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [toast, waitlistEntries]);

  if (isLoading) {
    return (
      <Flex align="center" justify="center" minH="60vh">
        <Spinner size="xl" />
      </Flex>
    );
  }

  return (
    <Box p={6} maxW="1200px" mx="auto">
      <Flex justify="space-between" align="center" mb={6} wrap="wrap" gap={4}>
        <Box>
          <Heading size="lg" mb={1}>
            Waitlist Admin Dashboard
          </Heading>
          <Text color="gray.600">
            Monitor signups, export data, and keep your waitlist clean.
          </Text>
        </Box>
       <HStack spacing={3}>
          <Button
            leftIcon={<Download size={18} />}
            colorScheme="blue"
            data-testid="export-waitlist-csv"
            onClick={exportCsv}
          >
            Export CSV
          </Button>
        </HStack>
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
        <Stat borderWidth="1px" borderRadius="lg" p={4}>
          <StatLabel>Total Waitlist</StatLabel>
          <StatNumber data-testid="waitlist-total">{totalCount}</StatNumber>
        </Stat>
        {Object.entries(roleBreakdown).map(([role, count]) => (
          <Stat key={role} borderWidth="1px" borderRadius="lg" p={4}>
            <StatLabel textTransform="capitalize">{role}</StatLabel>
            <StatNumber>{count}</StatNumber>
          </Stat>
        ))}
      </SimpleGrid>

      <Card>
        <CardHeader>
          <Heading size="md">Recent Signups</Heading>
        </CardHeader>
        <CardBody>
          {waitlistEntries.length === 0 ? (
            <Text color="gray.500">No waitlist entries yet.</Text>
          ) : (
            <Box overflowX="auto">
              <Table variant="striped" size="sm" data-testid="waitlist-table">
                <Thead>
                  <Tr>
                    <Th>Email</Th>
                    <Th>Name</Th>
                    <Th>Role</Th>
                    <Th>Referring Coach</Th>
                    <Th>Source</Th>
                    <Th>Created</Th>
                    <Th textAlign="center">Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {waitlistEntries.map(entry => (
                    <Tr key={entry.id}>
                      <Td>{entry.email}</Td>
                      <Td>{entry.name || '—'}</Td>
                      <Td textTransform="capitalize">{entry.role || '—'}</Td>
                      <Td>{entry.referringCoach || '—'}</Td>
                      <Td>{entry.source || '—'}</Td>
                      <Td>{formatTimestamp(entry.createdAt)}</Td>
                      <Td textAlign="center">
                        <IconButton
                          aria-label={`Delete ${entry.email}`}
                          size="sm"
                          colorScheme="red"
                          variant="ghost"
                          icon={<Trash2 size={16} />}
                          onClick={() => deleteEntry(entry)}
                        />
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </CardBody>
      </Card>
    </Box>
  );
};

export default WaitlistAdminDashboard;
